import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import {
  OpenItemsDialog,
  OpenItemsDialogChecklistSection,
} from '@/features/jobs/details/tax-type/components/OpenItems/index'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import { Button } from '@/components/Button'
import { ScrolledAction } from '@/features/jobs/details/components/PageHeader'
import {
  EmptyBusinessInfoCardListItem,
  EmptyChecklistDocumentCardListItem,
  EmptyDependentsInfoCardListItem,
  EmptyPersonalInfoCardListItem,
  EmptySpousalInfoCardListItem,
  EmptyTaxRefundMethodCardListItem,
} from '@/features/jobs/details/tax-type/info-gathering/components/ChecklistItemsSections'

interface OpenItemsDialogContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Open Items Dialog Container
 */
export const OpenItemsDialogContainer = observer(
  function OpenItemsDialogContainer({ vm }: OpenItemsDialogContainerProps) {
    const incompletePersonalItems =
      vm.infoGatheringVm.personalInfoSectionItemVms.filter(
        (item) => !item.isCompleted,
      )

    const incompleteBusinessItems =
      vm.infoGatheringVm.businessInfoSectionItemVms.filter(
        (item) => !item.isCompleted,
      )

    const incompleteDocumentItems = vm.infoGatheringVm.documentItemVms.filter(
      (item) => !item.isCompleted,
    )

    return (
      <OpenItemsDialog
        isOpen={vm.openItemsDialog.showing}
        onClose={vm.closeOpenItemsDialog}
        isChecklistComplete={!vm.infoGatheringVm.isChecklistIncomplete}
        testId="open-items-dialog"
        closeTestId="open-items-dialog-close-button"
      >
        {incompletePersonalItems.length > 0 && (
          <OpenItemsDialogChecklistSection title="Missing personal information">
            {incompletePersonalItems.map((itemVm) => {
              switch (itemVm.type) {
                case 'PersonalInfo': {
                  return (
                    <EmptyPersonalInfoCardListItem
                      key={itemVm.type}
                      variant="simple"
                      onAction={itemVm.showDialog}
                    />
                  )
                }
                case 'SpousalInfo': {
                  return (
                    <EmptySpousalInfoCardListItem
                      key={itemVm.type}
                      variant="simple"
                      onAction={itemVm.showDialog}
                    />
                  )
                }
                case 'DependentsInfo': {
                  return (
                    <EmptyDependentsInfoCardListItem
                      key={itemVm.type}
                      variant="simple"
                      onAction={itemVm.showDependentsDialog}
                    />
                  )
                }
                case 'PersonalTaxRefundMethod': {
                  return (
                    <EmptyTaxRefundMethodCardListItem
                      key={itemVm.type}
                      kind="personal"
                      variant="simple"
                      onAction={itemVm.showTaxRefundMethodsDialog}
                    />
                  )
                }
              }
            })}
          </OpenItemsDialogChecklistSection>
        )}

        {incompleteBusinessItems.length > 0 && (
          <OpenItemsDialogChecklistSection title="Missing business information">
            {incompleteBusinessItems.map((itemVm) => {
              switch (itemVm.type) {
                case 'BusinessInfo': {
                  return (
                    <EmptyBusinessInfoCardListItem
                      key={itemVm.type}
                      variant="simple"
                      onAction={itemVm.showBusinessProfilesDialog}
                    />
                  )
                }
                case 'BusinessTaxRefundMethod': {
                  return (
                    <EmptyTaxRefundMethodCardListItem
                      key={itemVm.type}
                      kind="business"
                      variant="simple"
                      onAction={itemVm.showTaxRefundMethodsDialog}
                    />
                  )
                }
              }
            })}
          </OpenItemsDialogChecklistSection>
        )}

        {incompleteDocumentItems.length > 0 && (
          <OpenItemsDialogChecklistSection title="Missing documents">
            {incompleteDocumentItems.map((itemVm) => (
              <EmptyChecklistDocumentCardListItem
                title={itemVm.item.name}
                description={itemVm.item.shortDescription}
                key={itemVm.item.id}
                variant="simple"
                onAction={itemVm.showDialog}
                testId={`open-items-dialog-document-${itemVm.item.input.documentTypeShortId}`}
              />
            ))}
          </OpenItemsDialogChecklistSection>
        )}
      </OpenItemsDialog>
    )
  },
)

interface OpenItemsActionCardContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Open Items Action Card Container
 */
export const OpenItemsActionCardContainer = observer(
  function OpenItemsActionCardContainer({
    vm,
  }: OpenItemsActionCardContainerProps) {
    return (
      <>
        <ActionCard
          kicker={
            vm.progressionVm.currentStep === 'OpenItems'
              ? 'There are open items'
              : 'Your checklist is almost complete'
          }
          title="Documents missing"
        >
          <Button
            onPress={vm.showOpenItemsDialog}
            label="Complete your checklist"
            testId="complete-checklist-button"
          />
        </ActionCard>
      </>
    )
  },
)

interface OpenItemsScrolledActionContainerProps {
  vm: TaxJobDetailsViewModel
}

/**
 * Scrolled action for the header.
 */
export const OpenItemsScrolledActionContainer = observer(
  function OpenItemsScrolledActionContainer({
    vm,
  }: OpenItemsScrolledActionContainerProps) {
    return (
      <ScrolledAction
        label="Complete your checklist"
        onPress={vm.openItemsDialog.show}
        testId="complete-checklist-scrolled-button"
      />
    )
  },
)
